import { useNavigate } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import { isAddressEqual } from 'viem';
import { useAccount, useConfig } from 'wagmi';
import { SchemaSignerDto } from '../../../generated/apiTypes';
import { useActiveSigner, useLinkedSigners } from '../../../hooks/useActiveSigner';
import { useDataForSigning } from '../../../hooks/useDataForSigning';
import { useModalState } from '../../../hooks/useModalState';
import { useFlattenedOrders, useOpenOrders } from '../../../hooks/useOrders';
import { useActiveSelectedSubaccount } from '../../../hooks/useSelectedSubaccount';
import { defaultSelectedTicker } from '../../../lib/config';
import { isUserRejectedError } from '../../../lib/errors';
import { formatExpirationDuration } from '../../../lib/formatters';
import { showToast } from '../../../lib/showToast';
import {
  disableOneClickTradingSuccessToastMessage,
  revokeSignerSuccessToastMessage,
} from '../../../lib/toasts';
import { assertRequiredData } from '../../../lib/typeGuards';
import { Alert } from '../../Alert';
import { Button } from '../../catalyst-ui-kit/button';
import { HexValue } from '../../HexValue/HexValue';
import { Loader } from '../../Loader';
import { RelativeDate } from '../../RelativeDate/RelativeDate';
import { useSettingsState } from '../SettingsState';
import { ALERT_CONTENT } from '../signerAlertContent';
import { useRevokeSigner } from '../signerHooks';

const SettingsRevokePanelUi: React.FC<{
  signerToRevoke: SchemaSignerDto | undefined;
  isActiveSigner: boolean;
  openOrderCount: number;
  hasRequiredDataErrors: boolean;
  requiredDataPending: boolean;
  revokeMutation: ReturnType<typeof useRevokeSigner>;
  isLoading: boolean;
  onDisableClick: () => void;
  onViewOpenOrders: () => void;
}> = ({
  signerToRevoke,
  isActiveSigner,
  hasRequiredDataErrors,
  requiredDataPending,
  revokeMutation,
  openOrderCount,
  isLoading,
  onDisableClick,
  onViewOpenOrders,
}) => {
  const action = isActiveSigner ? 'Disable' : 'Revoke';
  const mustCancelOrders = openOrderCount > 0;

  const alert = (() => {
    switch (true) {
      case hasRequiredDataErrors && !requiredDataPending: {
        return ALERT_CONTENT.REQUIRED_DATA_ERROR;
      }
      case mustCancelOrders: {
        return {
          variant: ALERT_CONTENT.HAS_OPEN_ORDERS.variant,
          message: ALERT_CONTENT.HAS_OPEN_ORDERS.getMessage(openOrderCount),
        };
      }

      case revokeMutation.error && !isUserRejectedError(revokeMutation.error): {
        return {
          variant: ALERT_CONTENT.ENABLE_ERROR.variant,
          message: ALERT_CONTENT.ENABLE_ERROR.getMessage(revokeMutation.error),
        };
      }
      case revokeMutation.isPending: {
        return ALERT_CONTENT.REVOKE_PENDING;
      }

      default: {
        return null;
      }
    }
  })();

  return (
    <div className="flex flex-col gap-3 sm:p-4">
      <p className="text-xs font-medium text-contentSecondary">
        {action === 'Disable' ? (
          <>
            This action will remove One-Click Trading on this device and switch to Manual Trading.
          </>
        ) : (
          <>This action will revoke the selected trading session.</>
        )}
      </p>
      {!isActiveSigner && signerToRevoke && (
        <div className="rounded border border-borderPrimary bg-backgroundPrimary p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-contentPrimary">Session Details</span>
          </div>
          <div className="mt-0.5 text-xs">
            <div className="flex justify-between">
              <span className="text-contentSecondary">Created:</span>
              <span className="text-right text-contentPrimary">
                <RelativeDate
                  timestamp={signerToRevoke.createdAt}
                  formatter={formatExpirationDuration}
                />
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-contentSecondary">Public Address:</span>
              <HexValue className="text-contentPrimary" value={signerToRevoke.signer} />
            </div>

            <div className="flex justify-between">
              <span className="text-contentSecondary">Expires:</span>
              <div className="flex flex-col items-end">
                <RelativeDate
                  timestamp={signerToRevoke.expiresAt}
                  formatter={formatExpirationDuration}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {alert && (
        <Alert className="overflow-x-auto" variant={alert.variant}>
          {alert.message}
        </Alert>
      )}
      {isLoading ? (
        <div
          className="h-9 w-full animate-pulse rounded-md bg-backgroundTertiary"
          aria-hidden="true"
          role="presentation"
        >
          <span className="sr-only">Loading...</span>
        </div>
      ) : mustCancelOrders ? (
        <Button outline onClick={onViewOpenOrders}>
          View Open Orders
        </Button>
      ) : (
        <Button
          outline
          disabled={Boolean(
            !signerToRevoke ||
              requiredDataPending ||
              hasRequiredDataErrors ||
              revokeMutation.isPending
          )}
          onClick={() => onDisableClick()}
          className="flex items-center justify-center gap-1"
        >
          {revokeMutation.isPending ? <Loader /> : null}
          {action === 'Disable' ? 'Disable One-Click Trading' : 'Revoke Trading Session'}
        </Button>
      )}
    </div>
  );
};

export const SettingsRevokePanel: React.FC = () => {
  const { address } = useAccount();
  const navigate = useNavigate();
  const setSettingsModal = useModalState((state) => state.setSettingsModal);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: activeSigner, isLoading: loadingActiveSigner } = useActiveSigner();
  const { data: linkedSigners, isLoading: loadingLinkedSigners } = useLinkedSigners();
  const config = useConfig();
  const {
    domain,
    signedTypedDataTypes,
    error: requiredDataError,
    isPending: requiredDataPending,
  } = useDataForSigning();
  const hasRequiredDataErrors = Boolean(requiredDataError);
  const { data: orderData, isPending: ordersPending } = useOpenOrders({
    subaccountId: selectedSubaccount?.id,
  });
  const orders = useFlattenedOrders(orderData?.pages);

  const addressToRevoke = useSettingsState(({ signerToRevoke }) => signerToRevoke);
  const resetPanels = useSettingsState((state) => state.resetPanels);
  const currentPanel = useSettingsState((state) => state.panelStack.at(-1));
  const popPanel = useSettingsState((state) => state.popPanel);

  const signerToRevoke = addressToRevoke
    ? linkedSigners?.find((signer) => isAddressEqual(signer.signer, addressToRevoke))
    : activeSigner;
  const isActiveSigner = activeSigner && signerToRevoke === activeSigner;

  const activeSignerOrders = activeSigner
    ? orders?.filter((order) => isAddressEqual(order.sender, activeSigner.signer))
    : orders;

  const revokeMutation = useRevokeSigner();
  const { isPending, reset: revokeReset } = revokeMutation;

  const mutationParams = {
    subaccountName: selectedSubaccount?.name,
    subaccountId: selectedSubaccount?.id,
    address,
    domain,
    signedTypedDataTypes,
    config,
    signerAddress: signerToRevoke?.signer,
    isActiveSigner: Boolean(isActiveSigner),
  };

  const handleViewOpenOrders = () => {
    void navigate({
      to: '/trade/$asset',
      params: { asset: defaultSelectedTicker },
      search: { tableTab: 'openOrders' },
    });
    setSettingsModal('closed');
    setTimeout(() => resetPanels(), 300);
  };

  useEffect(() => {
    if (currentPanel !== 'revoke' && currentPanel !== 'disable-one-click') {
      revokeReset();
    }
  }, [isPending, revokeReset, currentPanel]);

  // Go back to previous panel once mutation is complete.
  useEffect(() => {
    if (revokeMutation.isSuccess) {
      popPanel();
      revokeReset();
      showToast(
        isActiveSigner
          ? disableOneClickTradingSuccessToastMessage()
          : revokeSignerSuccessToastMessage()
      );
    }
  }, [revokeMutation.isSuccess, popPanel, revokeReset, isActiveSigner]);

  return (
    <SettingsRevokePanelUi
      openOrderCount={activeSignerOrders?.length ?? 0}
      signerToRevoke={signerToRevoke}
      isActiveSigner={Boolean(isActiveSigner)}
      revokeMutation={revokeMutation}
      hasRequiredDataErrors={hasRequiredDataErrors}
      requiredDataPending={requiredDataPending && ordersPending}
      isLoading={loadingActiveSigner || loadingLinkedSigners}
      onDisableClick={() => {
        assertRequiredData(mutationParams);
        revokeMutation.mutate(mutationParams);
      }}
      onViewOpenOrders={handleViewOpenOrders}
    />
  );
};
